import {
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Download, X } from 'lucide-react';

import { HtmlPreviewFrame } from '@modules/_shared/components/chat/HtmlPreviewFrame';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

export type HtmlPreviewState =
  | { kind: 'loading'; name: string }
  | { kind: 'ready'; name: string; html: string }
  | { kind: 'too-large'; name: string }
  | { kind: 'error'; name: string; message: string };

type Props = {
  preview: HtmlPreviewState | null;
  onDownload: () => void;
  onClose: () => void;
};

// Large dialog around the sandboxed HTML preview (HtmlPreviewFrame).
export const ArtifactPreviewDialog = ({
  preview,
  onDownload,
  onClose,
}: Props) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  return (
    <Dialog
      open={preview != null}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-label={t('Preview')}
      slotProps={{ paper: { sx: { height: '90vh' } } }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${colors.divider}`,
        }}
      >
        <Typography variant="subtitle2" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {preview?.name}
        </Typography>
        <Button
          size="small"
          onClick={onDownload}
          startIcon={<Download size={14} />}
        >
          {t('Download')}
        </Button>
        <IconButton size="small" onClick={onClose} title={t('Close')}>
          <X size={18} />
        </IconButton>
      </Stack>
      <div css={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {preview?.kind === 'ready' && (
          <HtmlPreviewFrame html={preview.html} title={preview.name} />
        )}
        {preview?.kind === 'loading' && (
          <Stack sx={{ alignItems: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {(preview?.kind === 'too-large' || preview?.kind === 'error') && (
          <Typography
            variant="body2"
            sx={{ p: 4, textAlign: 'center', color: colors.grey600 }}
          >
            {preview.kind === 'too-large'
              ? t('This file is too large to preview. Download it instead.')
              : preview.message}
          </Typography>
        )}
      </div>
    </Dialog>
  );
};
